import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

export const scrollToSection = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -64, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-white/20" data-testid="lang-toggle">
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          aria-label={l === "en" ? "Switch to English" : "Cambiar a Español"}
          className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${
            lang === l ? "bg-copper text-ink" : "text-bone/50 hover:text-bone"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  const links = [
    { label: t.nav.whoWeAre, href: "#about", testid: "nav-about-link" },
    { label: t.nav.services, href: "#services", testid: "nav-services-link" },
    { label: t.nav.advantage, href: "#advantage", testid: "nav-advantage-link" },
    { label: t.nav.reviews, href: "#testimonials", testid: "nav-testimonials-link" },
    { label: t.nav.team, href: "#team", testid: "nav-team-link" },
    { label: t.nav.calculator, href: "#calculator", testid: "nav-calculator-link" },
    { label: t.nav.netSheet, href: "#net", testid: "nav-net-link" },
    { label: t.nav.blog, href: "#blog", testid: "nav-blog-link" },
  ];

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex flex-col items-start group"
          aria-label="OPEAM Group — back to top"
        >
          <img
            src="/images/opeam-logo-a.png"
            alt="OPEAM Group"
            className="h-8 w-auto transition-opacity duration-300 group-hover:opacity-80"
          />
          <span
            data-testid="nav-tagline"
            className="mt-0.5 text-[8px] tracking-[0.3em] uppercase text-copper"
          >
            {t.hero.catch1} {t.hero.catch2}
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className="text-xs tracking-[0.2em] uppercase text-bone/60 hover:text-copper transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <LangToggle />
          <button
            data-testid="nav-contact-cta"
            onClick={() => go("#contact")}
            className="btn-swipe text-xs tracking-[0.2em] uppercase border border-copper text-copper px-6 py-2.5 transition-colors duration-300"
          >
            {t.nav.contact}
          </button>
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <LangToggle />
          <button
            data-testid="nav-mobile-toggle"
            className="text-bone"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-black/80 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.href)}
                  className="text-left text-sm tracking-[0.2em] uppercase text-bone/70 hover:text-copper transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-contact-cta-mobile"
                onClick={() => go("#contact")}
                className="text-left text-sm tracking-[0.2em] uppercase text-copper"
              >
                {t.nav.contact}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
